import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { arrayMove } from '@dnd-kit/sortable'
import { PlusIcon } from '@phosphor-icons/react'

import { Button, Divider, Text, ToastContext } from '@reveni-io/reveni-ui'

import LoadMore from '@/common/components/LoadMore/LoadMore'
import QueryFilter from '@/common/components/QueryFilter/QueryFilter'
import SaveChangesButtons from '@/common/components/SaveChangesButtons/SaveChangesButtons'
import UnsavedChangesAlert from '@/common/components/UnsavedChangesAlert/UnsavedChangesAlert'
import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useShowMore from '@/common/hooks/useShowMore/useShowMore'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import DeleteReturnInstructionModal from './DeleteReturnInstructionModal/DeleteReturnInstructionModal'
import useDeleteReturnInstructionSet from './hooks/useDeleteReturnInstructionSet/useDeleteReturnInstructionSet'
import useReturnInstructionSets from './hooks/useReturnInstructionSets/useReturnInstructionSets'
import SortableReturnInstructionList from './SortableReturnInstructionList/SortableReturnInstructionList'
import * as Styles from './styles'

const ReturnInstructionsConfiguration = () => {
  const { t } = useTranslation()
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const storeId = info?.selectedStore?.id
  const { hasPermission } = useUserPermissions()
  const isEditable = hasPermission(userPermissions.editLogistics)

  const {
    returnInstructions,
    setReturnInstructions,
    fetchingReturnInstructions,
    revertChanges,
    refetchReturnInstructions,
    hasLocalChanges,
    onSearch,
    loadMore,
    hasNextPage,
    totalCount,
    onSaveOrder,
    savingOrder,
    onEdit,
    onDuplicate,
    onCreate,
  } = useReturnInstructionSets({ storeId, setToastInfo })

  const { elementsToShow, hasMoreElements, showMore, totalElements, numberOfElementsToShow } = useShowMore({
    elements: returnInstructions,
    paginated: true,
    hasNextPage,
    onLoadMore: loadMore,
    totalCount,
  })

  const {
    showDeleteModal,
    closeDeleteModal,
    openDeleteModal,
    associatedDeliveryMethods,
    replacementId,
    setReplacementId,
    isFormValid,
    deleteInstruction,
    deletingInstruction,
    availableReplacements,
    loadingModalData,
  } = useDeleteReturnInstructionSet({ storeId, refetch: refetchReturnInstructions, setToastInfo })

  const onSortEnd = event => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    setReturnInstructions(prev => {
      const oldIndex = prev.findIndex(r => r.id === active.id)
      const newIndex = prev.findIndex(r => r.id === over.id)

      return [...arrayMove(prev, oldIndex, newIndex)]
    })
    document.body.style.cursor = 'inherit'
  }

  const onSortStart = () => {
    document.body.style.cursor = 'grabbing'
  }

  return (
    <Styles.Container>
      {hasLocalChanges && <UnsavedChangesAlert hasPermission={isEditable} />}
      <Text type="bodyMRegular">
        {t(
          'Define conjuntos de instrucciones de devolución para usar en tus métodos de devolución e informar a los clientes de lo que deben hacer para devolver sus productos a tu tienda.'
        )}
      </Text>
      <QueryFilter placeholder={t('Nombre de instrucciones de devolución')} onChange={onSearch} />
      <SortableReturnInstructionList
        returnInstructions={elementsToShow}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={openDeleteModal}
        onSortStart={onSortStart}
        onSortEnd={onSortEnd}
        loading={fetchingReturnInstructions}
        isEditable={isEditable}
      />
      <Divider />
      <LoadMore
        numberOfElementsToShow={numberOfElementsToShow}
        totalElements={totalElements}
        hasMoreElements={hasMoreElements}
        showMore={showMore}
      />
      {isEditable && (
        <Styles.ActionsContainer>
          <Button variant="text" onClick={onCreate}>
            <PlusIcon />
            {t('Añadir nuevas instrucciones de devolución')}
          </Button>
        </Styles.ActionsContainer>
      )}
      <SaveChangesButtons
        hasChanges={isEditable && hasLocalChanges}
        onSave={onSaveOrder}
        onRevertChanges={revertChanges}
        loading={savingOrder}
        disabled={savingOrder}
      />
      <DeleteReturnInstructionModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        associatedDeliveryMethods={associatedDeliveryMethods}
        availableReplacements={availableReplacements}
        replacementId={replacementId}
        onChangeReplacementId={setReplacementId}
        isFormValid={isFormValid}
        deleting={deletingInstruction}
        onDelete={deleteInstruction}
        loading={loadingModalData}
      />
    </Styles.Container>
  )
}

export default ReturnInstructionsConfiguration
